import React, { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import api from "@/api";
import { useFinanceAuth } from "@/context/FinanceAuthContext";
import { FileDown, Filter, Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const GROUP_OPTIONS = [
  ["none", "Nessuno (dettaglio)"],
  ["giorno", "Giorno"],
  ["settimana", "Settimana"],
  ["mese", "Mese"],
  ["lavoratore", "Lavoratore"],
  ["cantiere", "Cantiere"],
  ["societa", "Società"],
  ["mansione", "Mansione"],
];

export default function ReportOre() {
  const { pw, unlocked, clear, verifyAndSet } = useFinanceAuth();
  const [showPw, setShowPw] = useState(false);
  const [input, setInput] = useState("");

  const [filters, setFilters] = useState({
    da: "", a: "", cantiere_id: "", lavoratore_id: "", societa: "", mansione: "", raggruppa_per: "none",
  });

  const params = useMemo(
    () => new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "none"))).toString(),
    [filters]
  );

  const verify = useMutation({
    mutationFn: (password) => verifyAndSet(password),
    onSuccess: () => {
      setInput("");
      toast.success("Area sbloccata");
    },
    onError: () => toast.error("Password errata"),
  });

  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri"],
    queryFn: () => api.get("/cantieri").then((r) => r.data),
  });
  const { data: lavoratori = [] } = useQuery({
    queryKey: ["lavoratori"],
    queryFn: () => api.get("/lavoratori").then((r) => r.data),
  });
  const { data, isLoading } = useQuery({
    queryKey: ["report-ore", params, pw],
    queryFn: () => api.get(`/lavoratori/report/ore${params ? `?${params}` : ""}`, { headers: { "X-Finance-Password": pw } }).then((r) => r.data),
    enabled: unlocked && !!pw,
    retry: false,
  });

  // Memoized options MUST be declared before any conditional return
  const societa_list  = useMemo(() => Array.from(new Set(lavoratori.map((l) => l.societa).filter(Boolean))).sort(), [lavoratori]);
  const mansione_list = useMemo(() => Array.from(new Set(lavoratori.map((l) => l.mansione).filter(Boolean))).sort(), [lavoratori]);

  const optCantieri   = useMemo(() => [["", "Tutti"], ...cantieri.map((c) => [c.cantiere_id, c.nome])], [cantieri]);
  const optLavoratori = useMemo(() => [["", "Tutti"], ...lavoratori.map((l) => [l.lavoratore_id, `${l.nome} ${l.cognome}`])], [lavoratori]);
  const optSocieta    = useMemo(() => [["", "Tutte"], ...societa_list.map((s) => [s, s])], [societa_list]);
  const optMansione   = useMemo(() => [["", "Tutte"], ...mansione_list.map((m) => [m, m])], [mansione_list]);

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto mt-10">
        <div className="bg-white border border-slate-200 p-8 shadow-brutal">
          <div className="w-14 h-14 mx-auto bg-slate-900 flex items-center justify-center mb-4">
            <Lock className="w-7 h-7 text-orange-500" />
          </div>
          <h2 className="font-display text-2xl font-black text-center mb-2">Report ore riservato</h2>
          <p className="text-slate-600 text-sm text-center mb-6">
            Il report contiene dati economici sensibili (retribuzioni, costo aziendale).
            Inserisci la <b>password amministrativa</b> per accedere.
          </p>
          <div className="relative mb-3">
            <input
              type={showPw ? "text" : "password"} data-no-uppercase
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && input && verify.mutate(input)}
              placeholder="Password"
              className="w-full px-3 py-3 border border-slate-300 rounded-sm text-sm pr-10"
              autoFocus
            />
            <button onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button
            onClick={() => input && verify.mutate(input)}
            disabled={!input || verify.isPending}
            className="w-full py-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold rounded-sm shadow-brutal flex items-center justify-center gap-2"
          >
            <ShieldCheck className="w-4 h-4" /> Sblocca report
          </button>
        </div>
      </div>
    );
  }

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const raggruppato = data?.raggruppato;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2"><Lock className="w-3 h-3" /> Area riservata</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Report ore & costi lavoratori</h1>
          <p className="text-slate-600 text-sm mt-1">
            <b>Costo lavoratore su cantiere</b> = Ore lavorate × Costo orario aziendale
            <span className="text-slate-500"> (incluso contributi, TFR, ferie, mensilità, indennità)</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${backendUrl}/api/export/report-ore/excel${filters.cantiere_id ? `?cantiere_id=${filters.cantiere_id}` : ""}`}
            className="flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50"
          >
            <FileDown className="w-4 h-4" /> Esporta Excel
          </a>
          <button
            onClick={() => clear()}
            className="px-3 py-2 text-xs border border-slate-300 hover:bg-slate-50 rounded-sm flex items-center gap-1"
          >
            <Lock className="w-3 h-3" /> Blocca
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-600" />
          <div className="overline">Filtri & Raggruppamento</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          <FInput label="Dal" type="date" value={filters.da} onChange={(v) => setFilters({ ...filters, da: v })} />
          <FInput label="Al" type="date" value={filters.a} onChange={(v) => setFilters({ ...filters, a: v })} />
          <FSelect label="Cantiere" value={filters.cantiere_id} onChange={(v) => setFilters({ ...filters, cantiere_id: v })}
            options={optCantieri} />
          <FSelect label="Lavoratore" value={filters.lavoratore_id} onChange={(v) => setFilters({ ...filters, lavoratore_id: v })}
            options={optLavoratori} />
          <FSelect label="Società" value={filters.societa} onChange={(v) => setFilters({ ...filters, societa: v })}
            options={optSocieta} />
          <FSelect label="Mansione" value={filters.mansione} onChange={(v) => setFilters({ ...filters, mansione: v })}
            options={optMansione} />
          <FSelect label="Raggruppa per" value={filters.raggruppa_per} onChange={(v) => setFilters({ ...filters, raggruppa_per: v })}
            options={GROUP_OPTIONS} />
        </div>
      </div>

      {isLoading || !data ? (
        <div className="text-slate-500">Caricamento…</div>
      ) : raggruppato ? (
        <RaggruppatoView data={data} />
      ) : (
        <DettaglioView rows={data.righe} />
      )}
    </div>
  );
}

function DettaglioView({ rows }) {
  const tot = rows.reduce(
    (s, r) => ({
      ore_ord: s.ore_ord + r.ore_ordinarie,
      ore_str: s.ore_str + r.ore_straordinarie,
      costo_base: s.costo_base + r.costo_totale,
      trasferte: s.trasferte + r.trasferte,
      rimborsi: s.rimborsi + r.rimborsi,
      indennita: s.indennita + r.indennita_riga,
      costo_compl: s.costo_compl + r.costo_complessivo,
    }),
    { ore_ord: 0, ore_str: 0, costo_base: 0, trasferte: 0, rimborsi: 0, indennita: 0, costo_compl: 0 }
  );

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <StatCard label="Ore ordinarie" value={`${tot.ore_ord.toFixed(1)} h`} />
        <StatCard label="Ore straordinarie" value={`${tot.ore_str.toFixed(1)} h`} />
        <StatCard label="Costo base" value={eur(tot.costo_base)} />
        <StatCard label="Trasferte" value={eur(tot.trasferte)} />
        <StatCard label="Rimborsi" value={eur(tot.rimborsi)} />
        <StatCard label="Indennità" value={eur(tot.indennita)} />
        <StatCard label="Costo complessivo" value={eur(tot.costo_compl)} highlight />
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600 sticky top-0">
            <tr>
              <th className="text-left p-2">Data</th>
              <th className="text-left p-2">Cantiere</th>
              <th className="text-left p-2">Lavoratore</th>
              <th className="text-left p-2">Mansione</th>
              <th className="text-left p-2">Società</th>
              <th className="text-right p-2">Ore ord.</th>
              <th className="text-right p-2">Ore str.</th>
              <th className="text-right p-2">€/h az.</th>
              <th className="text-right p-2">Costo base</th>
              <th className="text-right p-2">Indennità</th>
              <th className="text-right p-2">Trasferte</th>
              <th className="text-right p-2">Rimborsi</th>
              <th className="text-right p-2">Costo complessivo</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.data}-${r.lavoratore_id || r.lavoratore_nome}-${r.cantiere_id || r.cantiere_nome}-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-2 font-mono">{r.data}</td>
                <td className="p-2">{r.cantiere_nome}</td>
                <td className="p-2 font-medium">{r.lavoratore_nome}</td>
                <td className="p-2">{r.mansione}</td>
                <td className="p-2">{r.societa || "—"}</td>
                <td className="p-2 text-right font-mono">{r.ore_ordinarie}</td>
                <td className="p-2 text-right font-mono">{r.ore_straordinarie}</td>
                <td className="p-2 text-right font-mono">{eur(r.costo_orario)}</td>
                <td className="p-2 text-right font-mono">{eur(r.costo_totale)}</td>
                <td className="p-2 text-right font-mono">{r.indennita_riga ? eur(r.indennita_riga) : "—"}</td>
                <td className="p-2 text-right font-mono">{r.trasferte ? eur(r.trasferte) : "—"}</td>
                <td className="p-2 text-right font-mono">{r.rimborsi ? eur(r.rimborsi) : "—"}</td>
                <td className="p-2 text-right font-mono font-bold">{eur(r.costo_complessivo)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="13" className="p-8 text-center text-slate-400">Nessun dato per i filtri selezionati</td></tr>
            )}
          </tbody>
          <tfoot className="bg-slate-900 text-white">
            <tr>
              <td colSpan="5" className="p-2 font-display font-bold">TOTALE</td>
              <td className="p-2 text-right font-mono font-bold">{tot.ore_ord.toFixed(1)}</td>
              <td className="p-2 text-right font-mono font-bold">{tot.ore_str.toFixed(1)}</td>
              <td></td>
              <td className="p-2 text-right font-mono font-bold">{eur(tot.costo_base)}</td>
              <td className="p-2 text-right font-mono font-bold">{eur(tot.indennita)}</td>
              <td className="p-2 text-right font-mono font-bold">{eur(tot.trasferte)}</td>
              <td className="p-2 text-right font-mono font-bold">{eur(tot.rimborsi)}</td>
              <td className="p-2 text-right font-mono font-black">{eur(tot.costo_compl)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

function RaggruppatoView({ data }) {
  const gruppi = data.gruppi || [];
  const tot = gruppi.reduce((s, g) => ({
    ore_totali: s.ore_totali + g.ore_totali,
    trasferte: s.trasferte + g.trasferte,
    rimborsi: s.rimborsi + g.rimborsi,
    indennita: s.indennita + g.indennita,
    costo_totale: s.costo_totale + g.costo_totale,
    costo_complessivo: s.costo_complessivo + g.costo_complessivo,
  }), { ore_totali: 0, trasferte: 0, rimborsi: 0, indennita: 0, costo_totale: 0, costo_complessivo: 0 });

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Gruppi" value={gruppi.length} />
        <StatCard label="Ore totali" value={`${tot.ore_totali.toFixed(1)} h`} />
        <StatCard label="Costo base" value={eur(tot.costo_totale)} />
        <StatCard label="Costo complessivo" value={eur(tot.costo_complessivo)} highlight />
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <div className="p-4 border-b border-slate-200">
          <div className="overline">Raggruppato per <b>{data.raggruppa_per}</b></div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Gruppo</th>
              <th className="text-right p-3">Rapportini</th>
              <th className="text-right p-3">Ore ord.</th>
              <th className="text-right p-3">Ore str.</th>
              <th className="text-right p-3">Ore totali</th>
              <th className="text-right p-3">Trasferte</th>
              <th className="text-right p-3">Rimborsi</th>
              <th className="text-right p-3">Indennità</th>
              <th className="text-right p-3">Costo base</th>
              <th className="text-right p-3">Costo complessivo</th>
            </tr>
          </thead>
          <tbody>
            {gruppi.map((g) => (
              <tr key={g.chiave} className="border-t border-slate-100">
                <td className="p-3 font-medium">{g.chiave}</td>
                <td className="p-3 text-right font-mono text-slate-500">{g.conteggio_rapportini}</td>
                <td className="p-3 text-right font-mono">{g.ore_ordinarie}</td>
                <td className="p-3 text-right font-mono">{g.ore_straordinarie}</td>
                <td className="p-3 text-right font-mono font-bold">{g.ore_totali}</td>
                <td className="p-3 text-right font-mono">{eur(g.trasferte)}</td>
                <td className="p-3 text-right font-mono">{eur(g.rimborsi)}</td>
                <td className="p-3 text-right font-mono">{eur(g.indennita)}</td>
                <td className="p-3 text-right font-mono">{eur(g.costo_totale)}</td>
                <td className="p-3 text-right font-mono font-bold text-orange-700">{eur(g.costo_complessivo)}</td>
              </tr>
            ))}
            {gruppi.length === 0 && <tr><td colSpan="10" className="p-8 text-center text-slate-400">Nessun dato</td></tr>}
          </tbody>
          <tfoot className="bg-slate-900 text-white">
            <tr>
              <td className="p-3 font-display font-bold">TOTALE</td>
              <td></td><td></td><td></td>
              <td className="p-3 text-right font-mono font-bold">{tot.ore_totali.toFixed(1)}</td>
              <td className="p-3 text-right font-mono font-bold">{eur(tot.trasferte)}</td>
              <td className="p-3 text-right font-mono font-bold">{eur(tot.rimborsi)}</td>
              <td className="p-3 text-right font-mono font-bold">{eur(tot.indennita)}</td>
              <td className="p-3 text-right font-mono font-bold">{eur(tot.costo_totale)}</td>
              <td className="p-3 text-right font-mono font-black">{eur(tot.costo_complessivo)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

function FInput({ label, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
    </div>
  );
}
function FSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
function StatCard({ label, value, highlight }) {
  return (
    <div className={`bg-white border p-4 ${highlight ? "border-orange-500 shadow-brutal" : "border-slate-200"}`}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-lg mt-1">{value}</div>
    </div>
  );
}
